import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Camera, ArrowClockwise, Check, MapPin, WarningCircle, CircleNotch } from "@phosphor-icons/react";

const LOCATION_OPTS = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };

function getLocation() {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Geolocation is not supported on this device."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          ts: new Date(pos.timestamp).toISOString(),
        }),
      (err) => reject(err),
      LOCATION_OPTS
    );
  });
}

/**
 * Module 7.2E4-Y — live camera + mandatory GPS custody evidence capture.
 * No gallery/file picker. Blocks capture if location permission is denied.
 */
export default function CameraEvidenceCapture({
  shipmentId, docType, label, packageId = null, awbId = null, roadLegId = null, stage = null, onCaptured, disabled = false,
  buttonClassName = "btn-primary inline-flex items-center gap-1.5 text-sm", buttonLabel,
}) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("init"); // init|location-error|camera-error|ready|preview|submitting
  const [errorMsg, setErrorMsg] = useState("");
  const [location, setLocation] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const blobRef = useRef(null);

  const attachVideo = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
      node.play().catch(() => {});
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setPhase("ready");
    } catch (e) {
      setErrorMsg("Camera is required for this evidence step.");
      setPhase("camera-error");
    }
  }, []);

  const beginFlow = useCallback(async () => {
    setPhase("init");
    setErrorMsg("");
    try {
      const loc = await getLocation();
      setLocation(loc);
    } catch (e) {
      setLocation(null); // best-effort: capture proceeds even without location
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorMsg("Camera is required for this evidence step.");
      setPhase("camera-error");
      return;
    }
    await startCamera();
  }, [startCamera]);

  const openDialog = () => {
    setOpen(true);
    beginFlow();
  };

  const closeAll = useCallback(() => {
    setOpen(false);
    stopCamera();
  }, [stopCamera]);

  // Revoke the preview object URL when it changes / on unmount (no state updates here).
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        blobRef.current = blob;
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
        stopCamera();
        setPhase("preview");
      },
      "image/jpeg",
      0.9
    );
  };

  const retake = async () => {
    blobRef.current = null;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    await startCamera();
  };

  const usePhoto = async () => {
    if (!blobRef.current) return;
    setPhase("submitting");
    // Re-acquire a FRESH location right before upload (best-effort; not required).
    let loc = null;
    try {
      loc = await getLocation();
      setLocation(loc);
    } catch (e) {
      loc = null;
      setLocation(null);
    }
    const fd = new FormData();
    fd.append("doc_type", docType);
    if (loc) {
      fd.append("latitude", String(loc.lat));
      fd.append("longitude", String(loc.lng));
      fd.append("accuracy_m", String(loc.accuracy));
      fd.append("geo_timestamp", loc.ts);
      fd.append("location_status", "granted");
    } else {
      fd.append("location_status", "unavailable");
    }
    if (stage) fd.append("stage", stage);
    if (packageId) fd.append("package_id", packageId);
    if (awbId) fd.append("awb_id", awbId);
    if (roadLegId) fd.append("road_leg_id", roadLegId);
    fd.append("file", blobRef.current, `${docType}.jpg`);
    try {
      const { data } = await api.post(`/shipments/${shipmentId}/evidence-photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${label || docType} captured`, {
        description: loc
          ? `Stamped ${data.captured_at_ist} · ${Number(loc.lat).toFixed(4)}, ${Number(loc.lng).toFixed(4)}`
          : `Stamped ${data.captured_at_ist} · Location unavailable`,
      });
      setOpen(false);
      onCaptured && (await onCaptured(data));
    } catch (e) {
      toast.error(e.displayMessage || "Evidence capture failed");
      setPhase("preview");
    }
  };

  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        disabled={disabled}
        onClick={openDialog}
        data-testid={`evidence-capture-open-${docType}`}
      >
        <Camera size={14} weight="bold" /> {buttonLabel || `Take photo`}
      </button>

      <Dialog open={open} onOpenChange={(v) => (v ? openDialog() : closeAll())}>
        <DialogContent className="sm:max-w-[440px]" data-testid={`evidence-capture-dialog-${docType}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera size={18} /> {label || docType}
            </DialogTitle>
            <DialogDescription>
              Live camera + location required — this is Air12 custody evidence (no gallery upload).
            </DialogDescription>
          </DialogHeader>

          {location && (
            <div className="text-xs text-[hsl(var(--muted))] flex items-center gap-1" data-testid="evidence-location-readout">
              <MapPin size={12} /> {location.lat.toFixed(5)}, {location.lng.toFixed(5)} · ±{Math.round(location.accuracy)}m
            </div>
          )}

          {(phase === "location-error" || phase === "camera-error") && (
            <div className="rounded-[2px] border border-red-300 bg-red-50 p-3 text-sm text-red-700 space-y-2" data-testid="evidence-block-message">
              <div className="flex items-start gap-2">
                <WarningCircle size={18} weight="bold" className="shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
              {phase === "location-error" && (
                <button className="btn-primary text-xs inline-flex items-center gap-1" onClick={beginFlow} data-testid="evidence-retry-location">
                  <ArrowClockwise size={12} /> Retry location
                </button>
              )}
            </div>
          )}

          {phase === "init" && (
            <div className="py-8 text-center text-sm text-[hsl(var(--muted))]">
              <CircleNotch size={20} className="animate-spin mx-auto mb-2" /> Requesting location & camera…
            </div>
          )}

          <div className={phase === "ready" ? "block" : "hidden"}>
            <video ref={attachVideo} playsInline muted className="w-full rounded-[2px] bg-black aspect-[3/4] object-cover" data-testid="evidence-video" />
            <button className="btn-primary w-full mt-3 inline-flex items-center justify-center gap-1.5" onClick={capture} data-testid="evidence-capture-shutter">
              <Camera size={16} weight="bold" /> Capture
            </button>
          </div>

          {phase === "preview" && previewUrl && (
            <div>
              <img src={previewUrl} alt="evidence preview" className="w-full rounded-[2px] bg-black aspect-[3/4] object-cover" data-testid="evidence-preview" />
              <div className="flex gap-2 mt-3">
                <button className="btn-ghost flex-1 inline-flex items-center justify-center gap-1.5" onClick={retake} data-testid="evidence-retake">
                  <ArrowClockwise size={15} /> Retake
                </button>
                <button className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5" onClick={usePhoto} data-testid="evidence-use-photo">
                  <Check size={15} weight="bold" /> Use photo
                </button>
              </div>
            </div>
          )}

          {phase === "submitting" && (
            <div className="py-8 text-center text-sm text-[hsl(var(--muted))]">
              <CircleNotch size={20} className="animate-spin mx-auto mb-2" /> Stamping & uploading evidence…
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
